import { useEffect, useState } from "react";
import styled from "styled-components";
import WebApp from "@twa-dev/sdk";
import { IoChevronForward, IoMoon, IoLanguage, IoVolumeMedium } from "react-icons/io5";
import { FaShoppingCart, FaLink, FaUsers, FaHeadset } from 'react-icons/fa';
import { APP_NAME } from '../constants/app';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { useSoundContext } from "../contexts/SoundContext";
import useUserStore from '../stores/userStore';
import useScrollToTop from '../hooks/useScrollToTop';

const API_URL = process.env.REACT_APP_API_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

// Скелетон-блок для универсального использования
const SkeletonBlock = styled.div`
	background: rgba(200,200,200,0.15);
	border-radius: 8px;
	min-height: ${props => props.height || '24px'};
	min-width: ${props => props.width || '40px'};
	width: ${props => props.width || '100%'};
	height: ${props => props.height || '24px'};
	margin: 0 auto;
	animation: skeleton-loading 1.2s infinite linear;
	@keyframes skeleton-loading {
		0% { opacity: 0.5; }
		50% { opacity: 1; }
		100% { opacity: 0.5; }
	}
`;

function ProfileSection() {
	const [activeTab, setActiveTab] = useState('profile');
	useScrollToTop([activeTab]);
	const navigate = useNavigate();
	const fetchUser = useUserStore(state => state.fetchUser);
	const user = useUserStore(state => state.user);
	const { theme, toggleTheme } = useThemeContext();
	const isDarkTheme = theme === 'dark';
	const { isSoundEnabled, toggleSound } = useSoundContext();
	const [language, setLanguage] = useState(() => localStorage.getItem('app_language') || 'ru');
	const { t, i18n } = useTranslation();

	// Загрузка данных пользователя
	useEffect(() => {
		const loadUserData = async () => {
			if (!user) {
				await fetchUser();
			}
		};
		loadUserData();
	}, [user, fetchUser]);

	const handleExternalLink = (url) => {
		if (WebApp.platform !== "unknown") {
			WebApp.openLink(url);
		} else {
			window.open(url, '_blank');
		}
	};

	const handleLanguageChange = (e) => {
		const newLang = e.target.value;
		i18n.changeLanguage(newLang);
		setLanguage(newLang);
		localStorage.setItem('app_language', newLang);
	};

	const links = [
		{
			id: 'buy-gems',
			title: t('buyTokens', { currency: t('currency_title') }),
			subtitle: t('profile.topUpBalance'),
			icon: <FaShoppingCart size={20} />,
			iconBg: '#5856d6',
			action: () => navigate('/packages')
		},
		{
			id: 'trade',
			title: t('profile.tradeLink'),
			subtitle: t('profile.connectSteam'),
			icon: <FaLink size={20} />,
			iconBg: '#34c759',
			action: () => navigate('/trade-url')
		},
		{
			id: 'community',
			title: `${t('profile.community')} ${APP_NAME}`,
			subtitle: t('profile.joinDiscussion'),
			icon: <FaUsers size={20} />,
			iconBg: '#007aff',
			action: () => handleExternalLink('https://t.me/skinforge')
		},
		{
			id: 'support',
			title: t('profile.support'),
			subtitle: t('profile.contactSupport'),
			icon: <FaHeadset size={20} />,
			iconBg: '#ff9500',
			action: () => navigate('/support')
		}
	];

	return (
		<Container>
			<ProfileHeader>
				<AvatarContainer>
					{!user ? (
						<SkeletonBlock width="96px" height="96px" />
					) : user.avatarUrl ? (
						<Avatar src={`${IMAGE_BASE_URL}${user.avatarUrl}`} alt="Profile" />
					) : (
						<DefaultAvatar>
							{user?.firstName?.[0] || '?'}
						</DefaultAvatar>
					)}
				</AvatarContainer>
				<UserName>
					{!user ? <SkeletonBlock width="120px" height="28px" /> : [user?.firstName, user?.lastName].filter(Boolean).join(' ')}
				</UserName>
			</ProfileHeader>

			<LinksSection>
				{links.map((link, index) => (
					<LinkCard
						key={link.id}
						onClick={link.action}
						isFirst={index === 0}
						isLast={index === links.length - 1}
					>
						<IconContainer bg={link.iconBg}>
							{link.icon}
						</IconContainer>
						<LinkInfo>
							<LinkTitle>{link.title}</LinkTitle>
							<LinkSubtitle>{link.subtitle}</LinkSubtitle>
						</LinkInfo>
						<ChevronIcon>
							<IoChevronForward size={20} />
						</ChevronIcon>
					</LinkCard>
				))}
			</LinksSection>

			<Section>
				<SectionTitle>{t('profile.settings')}</SectionTitle>
				<SettingsList>
					<SettingsItem>
						<SettingLeft>
							<IoLanguage size={20} />
							<SettingText>{t('profile.language')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<LanguageSelect value={language} onChange={handleLanguageChange}>
								<option value="ru">{t('languages.ru')}</option>
								<option value="en">{t('languages.en')}</option>
							</LanguageSelect>
						</SettingRight>
					</SettingsItem>

					<SettingsItem>
						<SettingLeft>
							<IoVolumeMedium size={20} />
							<SettingText>{t('profile.sound')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<Toggle isActive={isSoundEnabled} onClick={toggleSound} />
						</SettingRight>
					</SettingsItem>

					<SettingsItem>
						<SettingLeft>
							<IoMoon size={20} />
							<SettingText>{t('profile.darkTheme')}</SettingText>
						</SettingLeft>
						<SettingRight>
							<Toggle isActive={isDarkTheme} onClick={toggleTheme} />
						</SettingRight>
					</SettingsItem>
				</SettingsList>
			</Section>

			<Version>
				{t('common.version')} 1.0.0
			</Version>
		</Container >
	);
}

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;
`;

const ProfileHeader = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 32px;
	padding: 20px 0;
`;

const AvatarContainer = styled.div`
	width: 96px;
	height: 96px;
	margin-bottom: 16px;
	border-radius: 50%;
	overflow: hidden;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Avatar = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const DefaultAvatar = styled.div`
	width: 100%;
	height: 100%;
	background: ${props => props.theme.primary};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 36px;
	font-weight: 600;
`;

const UserName = styled.h1`
	font-size: 24px;
	font-weight: 600;
	color: ${props => props.theme.text.primary};
	margin: 0;
	text-align: center;
`;

const LinksSection = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	overflow: hidden;
	margin-bottom: 24px;
`;

const LinkCard = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	padding: 16px;
	background: none;
	border: none;
	border-bottom: ${props =>
		props.isLast ? 'none' : `1px solid ${props.theme.border}`};
	cursor: pointer;
	transition: background-color 0.2s;

	&:active {
		opacity: 0.7;
	}
`;

const IconContainer = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: ${props => props.bg};
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	margin-right: 16px;
	flex-shrink: 0;
`;

const LinkInfo = styled.div`
	flex: 1;
	text-align: left;
`;

const LinkTitle = styled.div`
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text.primary};
	margin-bottom: 4px;
`;

const LinkSubtitle = styled.div`
	font-size: 13px;
	color: ${props => props.theme.text.secondary};
`;

const ChevronIcon = styled.div`
	color: ${props => props.theme.text.secondary};
	margin-left: 8px;
	opacity: 0.5;
`;

const SectionTitle = styled.h2`
	font-size: 20px;
	font-weight: 600;
	color: ${props => props.theme.text.primary};
	margin: 0 0 16px 0;
`;

const Version = styled.div`
	text-align: center;
	color: ${props => props.theme.text.secondary};
	font-size: 13px;
	margin-top: 32px;
`;

const Section = styled.div`
	margin-bottom: 24px;
`;

const SettingsList = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 2px 8px ${props => props.theme.type === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.2)'};
`;

const SettingsItem = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px;
	cursor: pointer;
	color: ${props => props.theme.text.primary};
	background: ${props => props.theme.card.background};
	
	&:not(:last-child) {
		border-bottom: 1px solid ${props => props.theme.border};
	}

	&:active {
		opacity: 0.7;
	}
`;

const SettingLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	color: ${props => props.theme.text.primary};
`;

const SettingText = styled.span`
	font-size: 16px;
	color: ${props => props.theme.text.primary};
`;

const SettingRight = styled.div`
	display: flex;
	align-items: center;
	opacity: 1;
`;

const Toggle = styled.button`
	width: 44px;
	height: 24px;
	background: ${props => props.isActive ? props.theme.primary : props.theme.type === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
	border-radius: 12px;
	border: none;
	position: relative;
	cursor: pointer;
	transition: all 0.2s ease;
	padding: 2px;

	&:after {
		content: '';
		position: absolute;
		left: ${props => props.isActive ? 'calc(100% - 22px)' : '2px'};
		top: 2px;
		width: 20px;
		height: 20px;
		background: white;
		border-radius: 50%;
		transition: all 0.2s ease;
	}
`;

const LanguageSelect = styled.select`
	background: rgba(255, 255, 255, 0.1);
	border: none;
	border-radius: 8px;
	color: ${props => props.theme.text.primary};
	padding: 6px 12px;
	font-size: 14px;
	cursor: pointer;
	outline: none;

	option {
		background: ${props => props.theme.background};
		color: ${props => props.theme.text.primary};
	}
`;

export default ProfileSection;